// Owned-device → Buy deep-link (plan T12). When the device a user owns maps to
// an imported catalog listing, surface a "View in Buy" affordance that
// cross-navigates to the Buy tab's listing page. Renders nothing when the
// variant has no imported listing (legacy scan/Apple variants), so the link
// only appears where a real Buy page exists.
//
// Self-contained so ItemDetailScreen adds a single line — the owned-device
// mapping (variant_id → listing) and the cross-tab navigation both live here.
// Admin-gated by the caller (the Buy tab is admin-only, pre-launch).

import React, { useEffect, useMemo, useState } from 'react';
import { Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import { useTheme } from '../../styles/ThemeProvider';
import { resolveListingForVariant, type ResolvedListing } from '../../lib/catalogListings';
import type { MainTabParamList } from '../../types/navigation';

interface Props {
  variantId: string;
}

export default function OwnedDeviceBuyLink({ variantId }: Props) {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp<MainTabParamList>>();
  const [listing, setListing] = useState<ResolvedListing | null>(null);

  useEffect(() => {
    let cancelled = false;
    resolveListingForVariant(variantId).then((res) => {
      if (!cancelled) setListing(res);
    });
    return () => {
      cancelled = true;
    };
  }, [variantId]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        link: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginTop: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        text: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          flex: 1,
        },
      }),
    [theme],
  );

  // No imported listing for this variant → no affordance.
  if (!listing) return null;

  return (
    <TouchableOpacity
      style={styles.link}
      onPress={() =>
        // Cross-navigate to the Buy tab's listing page. The action bubbles up
        // from the current (Cache) stack to the tab navigator.
        navigation.navigate('Buy', {
          screen: 'CatalogListing',
          params: {
            listingId: listing.listingId,
            listingTitle: listing.listingTitle,
            variantId: listing.variantId,
          },
        })
      }
      accessibilityRole="button"
    >
      <Ionicons name="cart-outline" size={16} color={theme.colors.accent} />
      <Text style={styles.text}>View in Buy</Text>
      <Ionicons name="chevron-forward" size={16} color={theme.colors.textTertiary} />
    </TouchableOpacity>
  );
}
